import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import Tippy from '@tippyjs/react';
import {
  Breadcrumbs,
  Tabs,
  Avatar,
  Badge,
  Button,
  Tag,
  Modal,
  TextInput,
  Select,
  Alert,
  Table,
  EmptyState,
  Plus,
  Edit01,
  Trash01,
  User01,
} from '@opendate-app/setlist';
import { deriveNoteTitle } from '../../shared/components/CrmNotesCard';
import CrmPageLayout from '../../shared/components/CrmPageLayout';
import DealFormModal from '../../Deals/components/DealFormModal';
import { getStageBadgeConfig, getStageBadgeColor, getTypeLabel, getTypeBadgeColor } from '../../Deals/components/dealStages';
import { customFieldDisplayRows, renderCustomFieldRowValue } from '../../shared/utils/customFieldHelpers';
import StageProgressBar from './StageProgressBar';
import DealDonorCard from './DealDonorCard';
import DealCampaignCard from './DealCampaignCard';
import DealPipelineDetailsCard from './DealPipelineDetailsCard';
import DealConversionCard from './DealConversionCard';
import DealNotesTimeline from './DealNotesTimeline';
import crmLabels from '../../shared/utils/crmLabels';

const axios = require('axios').default;

function DealsDetailPage({
  team,
  deal,
  donor = null,
  campaign = null,
  conversion = null,
  csrfToken,
  currentUser,
  contactTypes = [],
  stages = [],
  types = [],
  statuses,
  owners = [],
  campaigns = [],
  funds = [],
  customFieldDefinitions = [],
}) {
  const labels = crmLabels(team);
  if (!team || !deal) return null;

  const [currentDeal, setCurrentDeal] = useState(deal);
  const [donorData, setDonorData] = useState(donor);
  const [campaignData, setCampaignData] = useState(campaign);
  const [contactPatchPending, setContactPatchPending] = useState(false);
  const contactPatchPendingRef = useRef(false);
  const createdFanIdRef = useRef(null);
  const [organizations, setOrganizations] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState(deal.selected_contacts || []);
  const [availableTags, setAvailableTags] = useState([]);
  const [companyNames, setCompanyNames] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  const customFieldRows = customFieldDisplayRows(customFieldDefinitions, currentDeal.custom_field_values);

  const [taggedItems, setTaggedItems] = useState([]);
  const [showTagPopup, setShowTagPopup] = useState(false);
  const [tagSearchValue, setTagSearchValue] = useState('');
  const tagPopupRef = useRef(null);

  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteTitle, setNoteTitle] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteErrors, setNoteErrors] = useState([]);
  const [editingNoteId, setEditingNoteId] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);

  const [showContactModal, setShowContactModal] = useState(false);
  const [contactModalTab, setContactModalTab] = useState('existing');

  const [addFirstName, setAddFirstName] = useState('');
  const [addLastName, setAddLastName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [addPhone, setAddPhone] = useState('');
  const [addCompanyName, setAddCompanyName] = useState(currentDeal.organization_name || '');
  const [addContactTypeIds, setAddContactTypeIds] = useState([]);
  const [addCompanyTypeaheadOpen, setAddCompanyTypeaheadOpen] = useState(false);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addErrors, setAddErrors] = useState([]);

  const [attachSearch, setAttachSearch] = useState('');
  const [attachResults, setAttachResults] = useState([]);
  const [attachSelectedId, setAttachSelectedId] = useState(null);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachSubmitting, setAttachSubmitting] = useState(false);
  const [attachError, setAttachError] = useState('');

  const name = currentDeal.name || '(No name)';
  const headerSubtitle = [
    currentDeal.organization_name,
    currentDeal.giving_campaign_name,
    currentDeal.owner_name ? `Owner: ${currentDeal.owner_name}` : null,
  ].filter(Boolean).join(' · ');
  const nameInitials = name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '?';

  useEffect(() => {
    setCurrentDeal(deal);
    setSelectedContacts(deal.selected_contacts || []);
  }, [deal]);

  useEffect(() => {
    if (!team || !team.id) return;

    const fetchPageData = async () => {
      if (csrfToken) {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      }

      try {
        const [orgsResponse, filterResponse] = await Promise.all([
          axios.get(`/teams/${team.id}/crm/organizations.json`, { params: { per_page: 500 } }),
          axios.get(`/teams/${team.id}/crm/fans/filter_values`),
        ]);

        const orgs = (orgsResponse.data.organizations || []).map((org) => ({ id: org.id, name: org.name }));
        setOrganizations(orgs);

        const filterValues = filterResponse.data || {};
        setCompanyNames((filterValues.company_names || []).map((c) => c.value));
      } catch (e) {
        toast.error(`Failed to load ${labels.deal.singular.toLowerCase()} details.`);
      }
    };

    fetchPageData();
  }, [team && team.id, csrfToken]);

  useEffect(() => {
    if (!team || !currentDeal || !currentDeal.id) return;

    const fetchTags = async () => {
      if (!csrfToken) return;

      try {
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
        const [itemsResponse, tagsResponse] = await Promise.all([
          axios.get(`/teams/${team.id}/tagged_items`, {
            params: {
              taggable_type: 'Deal',
              taggable_id: currentDeal.id,
            },
          }),
          axios.get(`/teams/${team.id}/crm/tags`, {
            params: { scope: 'Deal' },
          }),
        ]);

        setTaggedItems(itemsResponse.data || []);
        setAvailableTags(tagsResponse.data || []);
      } catch (err) {
        toast.error('Failed to load tags.');
      }
    };

    fetchTags();
  }, [team, currentDeal, csrfToken]);

  useEffect(() => {
    if (!showTagPopup) return;

    const handleClickOutside = (event) => {
      if (tagPopupRef.current && !tagPopupRef.current.contains(event.target)) {
        setShowTagPopup(false);
        setTagSearchValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showTagPopup]);

  useEffect(() => {
    if (!team || !team.id || !deal || !deal.id || !csrfToken) return;

    const fetchNotes = async () => {
      try {
        setNotesLoading(true);
        setNotesError('');

        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

        const response = await axios.get(`/teams/${team.id}/crm/notes.json`, {
          params: { notable_type: 'Deal', notable_id: deal.id },
        });
        setNotes(response.data || []);
      } catch (err) {
        setNotesError('Failed to load notes');
      } finally {
        setNotesLoading(false);
      }
    };

    fetchNotes();
  }, [team, deal, csrfToken]);

  function formatAmount(amount) {
    if (amount == null || amount === '') return null;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  }

  function formatCloseDate(dateStr) {
    if (!dateStr) return '—';
    const parts = dateStr.substring(0, 10).split('-');
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  const handleEditClick = () => {
    setShowEditModal(true);
  };

  const handleEditClose = () => {
    setShowEditModal(false);
  };

  const handleEditSave = (savedDeal) => {
    setCurrentDeal({ ...currentDeal, ...savedDeal });

    if (savedDeal.selected_contacts) {
      setSelectedContacts(savedDeal.selected_contacts);
    }

    refreshSidebar();
  };

  const handleOrganizationCreate = (newOrg) => {
    setOrganizations((prev) => [...prev, { id: newOrg.id, name: newOrg.name }].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleTagCreate = (newTag) => {
    setAvailableTags((prev) => [...prev, { id: newTag.id, name: newTag.name }].sort((a, b) => a.name.localeCompare(b.name)));
  };

  const handleToggleTag = async (tag, isActive, taggedItem) => {
    if (!team || !currentDeal || !csrfToken) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      if (isActive && taggedItem) {
        await axios.delete(`/teams/${team.id}/tagged_items/${taggedItem.id}.json`);
      } else {
        await axios.post(`/teams/${team.id}/tagged_items.json`, {
          tagged_item: {
            taggable_type: 'Deal',
            taggable_id: currentDeal.id,
            tag_id: tag.id,
          },
        });
      }

      const [itemsResponse, tagsResponse] = await Promise.all([
        axios.get(`/teams/${team.id}/tagged_items`, {
          params: {
            taggable_type: 'Deal',
            taggable_id: currentDeal.id,
          },
        }),
        axios.get(`/teams/${team.id}/crm/tags`, {
          params: { scope: 'Deal' },
        }),
      ]);

      setTaggedItems(itemsResponse.data || []);
      setAvailableTags(tagsResponse.data || []);
    } catch (err) {
      toast.error('Failed to update tag.');
    }
  };

  const handleCreateAndAddTag = async (tagName) => {
    if (!team || !currentDeal || !csrfToken || !tagName.trim()) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const tagResponse = await axios.post(`/teams/${team.id}/crm/tags`, {
        tag: { name: tagName.trim(), scope: 'Deal' },
      });

      const newTag = tagResponse.data;

      await axios.post(`/teams/${team.id}/tagged_items.json`, {
        tagged_item: {
          taggable_type: 'Deal',
          taggable_id: currentDeal.id,
          tag_id: newTag.id,
        },
      });

      const [itemsResponse, tagsResponse] = await Promise.all([
        axios.get(`/teams/${team.id}/tagged_items`, {
          params: {
            taggable_type: 'Deal',
            taggable_id: currentDeal.id,
          },
        }),
        axios.get(`/teams/${team.id}/crm/tags`, {
          params: { scope: 'Deal' },
        }),
      ]);

      setTaggedItems(itemsResponse.data || []);
      setAvailableTags(tagsResponse.data || []);
      setTagSearchValue('');
    } catch (err) {
      toast.error('Failed to create tag.');
    }
  };

  const openContactModal = () => {
    setContactModalTab('existing');
    createdFanIdRef.current = null;

    setAddFirstName('');
    setAddLastName('');
    setAddEmail('');
    setAddPhone('');
    setAddCompanyName(currentDeal.organization_name || '');
    setAddContactTypeIds([]);
    setAddCompanyTypeaheadOpen(false);
    setAddErrors([]);
    setAddSubmitting(false);

    setAttachSearch('');
    setAttachResults([]);
    setAttachSelectedId(null);
    setAttachError('');
    setAttachLoading(false);
    setAttachSubmitting(false);

    setShowContactModal(true);
  };

  const closeContactModal = () => {
    if (addSubmitting || attachSubmitting) return;
    setShowContactModal(false);
  };

  const canSubmitCreateContactForm = addFirstName && addFirstName.trim().length > 0;

  const applyDealResponse = (data) => {
    setCurrentDeal(data);
    setSelectedContacts(data.selected_contacts || []);
  };

  const patchDeal = async (dealPart) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    contactPatchPendingRef.current = true;
    setContactPatchPending(true);
    try {
      const response = await axios.patch(
        `/teams/${team.id}/crm/deals/${currentDeal.id}.json`,
        { deal: dealPart },
      );
      applyDealResponse(response.data);
    } finally {
      contactPatchPendingRef.current = false;
      setContactPatchPending(false);
    }
  };

  const refreshSidebar = async () => {
    try {
      const response = await axios.get(`/teams/${team.id}/crm/deals/${currentDeal.id}.json`);
      const { donor: refreshedDonor, campaign: refreshedCampaign, ...dealFields } = response.data;
      setCurrentDeal(dealFields);
      setSelectedContacts(dealFields.selected_contacts || []);
      setDonorData(refreshedDonor);
      setCampaignData(refreshedCampaign);
    } catch (err) {
      toast.error(`Failed to refresh ${labels.deal.singular.toLowerCase()} details.`);
    }
  };

  const addDealContact = async (fanId) => {
    const id = Number(fanId);
    if (!id || id === currentDeal.primary_fan_id) return;
    const current = (currentDeal.additional_fan_ids || []).map(Number);
    const next = Array.from(new Set([...current, id]));
    await patchDeal({ additional_fan_ids: next });
  };

  const handleCreateContact = async (e) => {
    e.preventDefault();
    if (!canSubmitCreateContactForm) return;
    if (!team || !team.id || !csrfToken) return;
    if (contactPatchPendingRef.current) return;

    if (!addFirstName.trim()) {
      setAddErrors(['First name is required']);
      return;
    }

    try {
      setAddSubmitting(true);
      setAddErrors([]);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const payload = {
        fan: {
          first_name: addFirstName.trim(),
          last_name: addLastName.trim() || null,
          email: addEmail.trim() || null,
          phone_number: addPhone.trim() || null,
          company_name: addCompanyName.trim() || null,
          contact_type_ids: addContactTypeIds,
        },
      };

      let fanId = createdFanIdRef.current;
      if (!fanId) {
        const response = await axios.post(
          `/teams/${team.id}/crm/fans.json`,
          payload,
        );
        fanId = response.data.id;
        createdFanIdRef.current = fanId;
      }

      await addDealContact(fanId);
      createdFanIdRef.current = null;
      setShowContactModal(false);
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setAddErrors(resp.data);
      } else if (createdFanIdRef.current) {
        setAddErrors([`${labels.contact.singular} was created but could not be added to the ${labels.deal.singular.toLowerCase()}. Click Create ${labels.contact.singular.toLowerCase()} to retry.`]);
      } else {
        setAddErrors(['Failed to create contact']);
      }
    } finally {
      setAddSubmitting(false);
    }
  };

  const handleAttachSearch = async () => {
    if (!team || !team.id) return;

    const query = attachSearch.trim();
    if (!query) {
      setAttachResults([]);
      setAttachSelectedId(null);
      setAttachError('');
      return;
    }

    try {
      setAttachLoading(true);
      setAttachError('');

      const response = await axios.get(`/teams/${team.id}/crm/fans/autocomplete`, {
        params: {
          name: query,
          omit_ids: selectedContacts.map((c) => c.id),
        },
      });

      const results = response.data || [];
      setAttachResults(results);
      if (!results.find((r) => r.id === attachSelectedId)) {
        setAttachSelectedId(null);
      }
    } catch (err) {
      setAttachError('Failed to search contacts');
    } finally {
      setAttachLoading(false);
    }
  };

  const handleAttachContact = async (e) => {
    e.preventDefault();
    if (!team || !team.id || !csrfToken || !attachSelectedId) return;
    if (contactPatchPendingRef.current) return;

    try {
      setAttachSubmitting(true);
      setAttachError('');

      await addDealContact(attachSelectedId);

      setShowContactModal(false);
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setAttachError(resp.data.join(', '));
      } else {
        setAttachError('Failed to add contact');
      }
    } finally {
      setAttachSubmitting(false);
    }
  };

  const handleDetachContact = async (event, contact) => {
    event.stopPropagation();
    if (!team || !team.id || !csrfToken || !contact || !contact.id) return;
    if (contactPatchPendingRef.current) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Remove this ${labels.contact.singular.toLowerCase()} from this ${labels.deal.singular.toLowerCase()}? They will remain in your ${labels.contact.plural.toLowerCase()} list.`,
    );
    if (!confirmed) return;

    try {
      const next = (currentDeal.additional_fan_ids || [])
        .map(Number)
        .filter((id) => id !== Number(contact.id));
      await patchDeal({ additional_fan_ids: next });
    } catch (err) {
      toast.error('Failed to remove contact');
    }
  };

  const refreshNotes = async () => {
    try {
      const response = await axios.get(`/teams/${team.id}/crm/notes.json`, {
        params: { notable_type: 'Deal', notable_id: deal.id },
      });
      setNotes(response.data || []);
    } catch (err) {
      toast.error('Failed to refresh notes.');
    }
  };

  const handleCreateNote = async (body) => {
    if (!team || !team.id || !csrfToken || !deal || !deal.id) {
      throw new Error('Missing context to create note');
    }
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
    await axios.post(`/teams/${team.id}/crm/notes.json`, {
      note: {
        title: deriveNoteTitle(body),
        body,
        notable_type: 'Deal',
        notable_id: deal.id,
      },
    });
    await refreshNotes();
  };

  const canSubmitNoteForm = noteBody && noteBody.trim().length > 0;

  const handleSaveNote = async (e) => {
    e.preventDefault();
    if (!canSubmitNoteForm) return;
    if (!team || !team.id || !csrfToken || !deal || !deal.id) return;

    if (!noteBody.trim()) {
      setNoteErrors(['Note content is required']);
      return;
    }

    try {
      setNoteSubmitting(true);
      setNoteErrors([]);
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

      const noteData = {
        note: {
          title: noteTitle.trim() || null,
          body: noteBody.trim(),
          notable_type: 'Deal',
          notable_id: deal.id,
        },
      };

      if (editingNoteId) {
        await axios.patch(`/teams/${team.id}/crm/notes/${editingNoteId}.json`, noteData);
      } else {
        await axios.post(`/teams/${team.id}/crm/notes.json`, noteData);
      }

      await refreshNotes();
      setShowNoteModal(false);
      setEditingNoteId(null);
    } catch (err) {
      const resp = err.response;
      if (resp && Array.isArray(resp.data)) {
        setNoteErrors(resp.data);
      } else {
        setNoteErrors([editingNoteId ? 'Failed to update note' : 'Failed to create note']);
      }
    } finally {
      setNoteSubmitting(false);
    }
  };

  const handleEditNote = (note) => {
    setNoteTitle(note.title || '');
    const plainBody = (note.sanitized_body || note.body || '')
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    setNoteBody(plainBody);
    setEditingNoteId(note.id);
    setNoteErrors([]);
    setShowNoteModal(true);
  };

  const handleDeleteNote = async (note) => {
    if (!team || !team.id || !csrfToken || !note || !note.id) return;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm('Are you sure you want to delete this note?');
    if (!confirmed) return;

    try {
      axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
      await axios.delete(`/teams/${team.id}/crm/notes/${note.id}.json`);
      await refreshNotes();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Failed to delete note');
    }
  };

  const renderStageBadge = (stage) => (
    <Badge color={getStageBadgeColor(stage)} dot>{getStageBadgeConfig(stage).label}</Badge>
  );

  return (
    <CrmPageLayout team={team} activeSidebarKey="deals">
          <div className="card mb-3 cdp-main-card">
            <div className="card-body">
              <div style={{ marginBottom: 12 }}>
                <Breadcrumbs
                  items={[
                    { label: labels.deal.plural, href: `/teams/${team.id}/crm/deals` },
                    { label: `${labels.deal.singular} Details` },
                  ]}
                />
              </div>

              <div className="d-flex justify-content-between align-items-start" style={{ gap: 16 }}>
                <div className="d-flex align-items-center" style={{ gap: 16, minWidth: 0 }}>
                  <Avatar initials={nameInitials} alt={name} size="lg" />
                  <div style={{ minWidth: 0 }}>
                    <div className="d-flex align-items-center" style={{ gap: 12, flexWrap: 'wrap' }}>
                      <h1 className="mb-0" style={{ fontSize: 24, fontWeight: 600, color: 'var(--sl-text-primary)' }}>
                        {name}
                      </h1>
                      <div className="deal-header-badges">
                        {currentDeal.deal_type && (
                          <Badge color={getTypeBadgeColor(currentDeal.deal_type)} dot>
                            {getTypeLabel(currentDeal.deal_type, types)}
                          </Badge>
                        )}
                        {renderStageBadge(currentDeal.stage)}
                      </div>
                    </div>
                    {headerSubtitle && <div className="deal-header-subtitle">{headerSubtitle}</div>}
                  </div>
                </div>
                <Button variant="primary" icon={Edit01} onClick={handleEditClick} className="flex-shrink-0">
                  {`Edit ${labels.deal.singular.toLowerCase()}`}
                </Button>
              </div>

              <StageProgressBar dealType={currentDeal.deal_type} stage={currentDeal.stage} />

              <div style={{ height: 20 }} />

              <Tabs
                aria-label={`${labels.deal.singular} detail sections`}
                collapseBelow={0}
                value={activeTab}
                onChange={setActiveTab}
                items={[
                  { value: 'overview', label: 'Overview' },
                  { value: 'contacts', label: labels.contact.plural },
                ]}
              />

              {activeTab === 'overview' && (
                <div className="row" style={{ marginTop: 4 }}>
                  <div className="col-lg-8">
                    <div className="row" style={{ rowGap: 24 }}>
                      <div className="col-md-4">
                        <div className="cdp-field-label">Name</div>
                        <div>{currentDeal.name || '—'}</div>
                      </div>
                      <div className="col-md-4">
                        <div className="cdp-field-label">{currentDeal.deal_type === 'fundraising' ? 'Ask amount' : 'Amount'}</div>
                        <div>{formatAmount(currentDeal.amount) || '—'}</div>
                      </div>
                      <div className="col-md-4">
                        <div className="cdp-field-label">Stage</div>
                        <div>{renderStageBadge(currentDeal.stage)}</div>
                      </div>
                      <div className="col-md-4">
                        <div className="cdp-field-label">Close date</div>
                        <div>{formatCloseDate(currentDeal.close_date)}</div>
                      </div>
                      <div className="col-md-4">
                        <div className="cdp-field-label">Type</div>
                        <div>{getTypeLabel(currentDeal.deal_type, types)}</div>
                      </div>
                      <div className="col-md-4">
                        <div className="cdp-field-label">{labels.organization.singular}</div>
                        <div>
                          {currentDeal.organization_name && currentDeal.organization_id ? (
                            <a href={`/teams/${team.id}/crm/organizations/${currentDeal.organization_id}`}>
                              {currentDeal.organization_name}
                            </a>
                          ) : (
                            '—'
                          )}
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="cdp-field-label">Primary Contact</div>
                        <div>{currentDeal.primary_fan_name || '—'}</div>
                      </div>
                      {currentDeal.description && (
                        <div className="col-12">
                          <div className="cdp-field-label">Description</div>
                          <div>{currentDeal.description}</div>
                        </div>
                      )}
                    </div>

                    <div style={{ marginTop: 24 }}>
                      <DealNotesTimeline
                        notes={notes}
                        loading={notesLoading}
                        error={notesError}
                        onCreateNote={handleCreateNote}
                        onEditNote={handleEditNote}
                        onDeleteNote={handleDeleteNote}
                        canManageNotes
                      />
                    </div>
                  </div>

                  <div className="col-lg-4 deal-overview-sidebar">
                    <DealDonorCard donor={donorData} teamId={team.id} title={currentDeal.deal_type === 'fundraising' ? 'Donor' : labels.contact.singular} />
                    <DealPipelineDetailsCard deal={currentDeal} />
                    <DealConversionCard deal={currentDeal} conversion={conversion} csrfToken={csrfToken} onConverted={refreshSidebar} />
                    <DealCampaignCard campaign={campaignData} teamId={team.id} />

                    <div className="deal-side-card" ref={tagPopupRef} style={{ position: 'relative' }}>
                      <div className="deal-side-card-title">Tags</div>
                      <div className="deal-side-tags">
                        {taggedItems.filter((item) => item.tag).map((item) => {
                          const tag = item.tag;
                          const creatorName = item.created_by && item.created_by.name ? item.created_by.name : null;
                          const canManage = currentUser && currentUser.can_manage_tagged_items;
                          return (
                            <Tippy key={tag.id} content={creatorName ? `Added by ${creatorName}` : 'Tag'}>
                              <span style={{ display: 'inline-flex' }}>
                                <Tag
                                  size="sm"
                                  onDismiss={canManage ? (e) => { e.stopPropagation(); handleToggleTag(tag, true, item); } : undefined}
                                >
                                  {tag.name}
                                </Tag>
                              </span>
                            </Tippy>
                          );
                        })}
                        {taggedItems.filter((item) => item.tag).length === 0 && (!currentUser || !currentUser.can_manage_tagged_items) && (
                          <span style={{ color: 'var(--sl-text-disabled)', fontSize: 12, fontStyle: 'italic' }}>No tags</span>
                        )}
                        {currentUser && currentUser.can_manage_tagged_items && (
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={Plus}
                            onClick={(e) => { e.stopPropagation(); setShowTagPopup(true); }}
                          >
                            Add Tag
                          </Button>
                        )}
                      </div>

                      {showTagPopup && currentUser && currentUser.can_manage_tagged_items && (
                        <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 1000, marginTop: 8, backgroundColor: 'var(--sl-bg-primary)', border: '1px solid var(--sl-border-primary)', borderRadius: 8, padding: 16, minWidth: 280, maxWidth: 360, boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.05), 0px 10px 15px -3px rgba(16, 24, 40, 0.1)' }}>
                          <div style={{ fontSize: 14, color: 'var(--sl-text-primary)', marginBottom: 12, fontWeight: 600 }}>Manage Tags</div>
                          <div style={{ marginBottom: 12 }}>
                            <TextInput
                              size="sm"
                              placeholder="Search or create tag…"
                              value={tagSearchValue}
                              onChange={(e) => setTagSearchValue(e.target.value)}
                            />
                          </div>
                          <div style={{ maxHeight: 250, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {availableTags.filter((tag) => tag.name.toLowerCase().includes(tagSearchValue.toLowerCase())).map((tag) => {
                              const taggedItem = taggedItems.find((item) => item.tag && item.tag.id === tag.id);
                              const isActive = !!taggedItem;
                              return (
                                <button
                                  type="button"
                                  key={tag.id}
                                  onClick={() => handleToggleTag(tag, isActive, taggedItem)}
                                  style={{ padding: '4px 10px', fontSize: 12, borderRadius: 14, cursor: 'pointer', backgroundColor: isActive ? 'var(--sl-bg-brand-primary)' : 'var(--sl-bg-tertiary)', border: '1px solid var(--sl-border-secondary)', color: isActive ? 'var(--sl-fg-brand)' : 'var(--sl-text-secondary)', fontWeight: 500 }}
                                >
                                  {tag.name}
                                </button>
                              );
                            })}
                            {tagSearchValue && !availableTags.find((tag) => tag.name.toLowerCase() === tagSearchValue.toLowerCase()) && (
                              <button
                                type="button"
                                onClick={() => handleCreateAndAddTag(tagSearchValue)}
                                style={{ padding: '4px 10px', fontSize: 12, borderRadius: 14, cursor: 'pointer', backgroundColor: 'var(--sl-bg-success-primary)', border: '1px solid var(--sl-border-secondary)', color: 'var(--sl-text-success)', fontWeight: 500 }}
                              >
                                + Create &quot;{tagSearchValue}&quot;
                              </button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {customFieldRows.length > 0 && (
                      <div className="deal-side-card">
                        <div className="deal-side-card-title">Custom Fields</div>
                        {customFieldRows.map((row) => (
                          <div className="deal-side-row" key={row.definition.id}>
                            <span className="deal-side-row-label">{row.definition.name}</span>
                            <div className="deal-side-row-value crm-custom-field-value">{renderCustomFieldRowValue(row, team?.id)}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'contacts' && (
                <div className="mt-3">
                  <div className="card">
                    <div className="card-body p-0">
                      <div className="border-bottom px-4 py-3 d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0">{labels.contact.plural}</h2>
                        <Button
                          variant="primary"
                          size="sm"
                          icon={Plus}
                          onClick={openContactModal}
                          disabled={contactPatchPending}
                        >
                          Add contact
                        </Button>
                      </div>

                      <div style={{ padding: '4px 8px' }}>
                        <Table
                          columns={[
                            {
                              key: 'name',
                              header: 'Name',
                              primary: true,
                              render: (contact) => (
                                <Button as="a" href={`/teams/${team.id}/crm/fans/${contact.id}`} variant="link-color">
                                  {contact.name || '(No name)'}
                                </Button>
                              ),
                            },
                            { key: 'email', header: 'Email', render: (contact) => contact.email || '—' },
                            {
                              key: 'role',
                              header: 'Role',
                              render: (contact) => (Number(contact.id) === Number(currentDeal.primary_fan_id) ? `Primary ${labels.contact.singular.toLowerCase()}` : labels.contact.singular),
                            },
                            {
                              key: 'actions',
                              header: '',
                              align: 'right',
                              width: '48px',
                              render: (contact) => (Number(contact.id) === Number(currentDeal.primary_fan_id) ? null : (
                                <Button
                                  variant="link-destructive"
                                  size="sm"
                                  icon={Trash01}
                                  aria-label={`Remove ${labels.contact.singular.toLowerCase()} from ${labels.deal.singular.toLowerCase()}`}
                                  onClick={(event) => handleDetachContact(event, contact)}
                                  disabled={contactPatchPending}
                                />
                              )),
                            },
                          ]}
                          data={selectedContacts}
                          getRowKey={(contact) => contact.id}
                          empty={<EmptyState size="sm" icon={User01} title="No contacts yet." />}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

      <DealFormModal
        isOpen={showEditModal}
        onClose={handleEditClose}
        onSaved={handleEditSave}
        deal={currentDeal}
        team={team}
        csrfToken={csrfToken}
        stages={stages}
        statuses={statuses}
        owners={owners}
        campaigns={campaigns}
        funds={funds}
        organizations={organizations}
        selectedContacts={selectedContacts}
        availableTags={availableTags}
        customFieldDefinitions={customFieldDefinitions}
        onOrganizationCreate={handleOrganizationCreate}
        onTagCreate={handleTagCreate}
      />

      <Modal
        open={showContactModal}
        onClose={closeContactModal}
        icon={false}
        width={720}
        title="Add contact"
        footer={contactModalTab === 'new' ? (
          <>
            <Button variant="secondary" onClick={closeContactModal} disabled={addSubmitting}>Cancel</Button>
            <Button variant="primary" onClick={handleCreateContact} disabled={!canSubmitCreateContactForm || addSubmitting}>
              {addSubmitting ? 'Saving…' : 'Create contact'}
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={closeContactModal} disabled={attachSubmitting}>Cancel</Button>
            <Button variant="primary" onClick={handleAttachContact} disabled={attachSubmitting || !attachSelectedId}>
              {attachSubmitting ? 'Attaching…' : 'Attach contact'}
            </Button>
          </>
        )}
      >
        <Tabs
          variant="button-border"
          collapseBelow={0}
          value={contactModalTab}
          onChange={setContactModalTab}
          items={[
            { value: 'new', label: 'Create new' },
            { value: 'existing', label: 'Add existing' },
          ]}
        />

        {contactModalTab === 'new' && (
          <div style={{ marginTop: 16 }}>
            {addErrors.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <Alert color="error" description={addErrors.join(', ')} />
              </div>
            )}

            <div className="row" style={{ rowGap: 12 }}>
              <div className="col-md-6">
                <TextInput label="First name" required value={addFirstName} onChange={(e) => setAddFirstName(e.target.value)} />
              </div>
              <div className="col-md-6">
                <TextInput label="Last name" value={addLastName} onChange={(e) => setAddLastName(e.target.value)} />
              </div>
              <div className="col-md-6">
                <TextInput label="Email" type="email" value={addEmail} onChange={(e) => setAddEmail(e.target.value)} />
              </div>
              <div className="col-md-6">
                <TextInput label="Phone" type="tel" value={addPhone} onChange={(e) => setAddPhone(e.target.value)} />
              </div>
              <div className="col-md-6" style={{ position: 'relative' }}>
                <TextInput
                  label="Company name"
                  value={addCompanyName}
                  autoComplete="off"
                  onChange={(e) => { setAddCompanyName(e.target.value); setAddCompanyTypeaheadOpen(true); }}
                  onFocus={() => setAddCompanyTypeaheadOpen(true)}
                  onBlur={() => { setTimeout(() => setAddCompanyTypeaheadOpen(false), 150); }}
                />
                {addCompanyTypeaheadOpen && companyNames && companyNames.length > 0 && (
                  <div className="dropdown-menu show w-100 shadow-sm" style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {companyNames
                      .filter((cname) => (!addCompanyName ? true : (cname || '').toLowerCase().includes(addCompanyName.toLowerCase())))
                      .slice(0, 10)
                      .map((cname) => (
                        <button
                          key={cname}
                          type="button"
                          className="dropdown-item"
                          onMouseDown={(e) => { e.preventDefault(); setAddCompanyName(cname); setAddCompanyTypeaheadOpen(false); }}
                        >
                          {cname}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <div className="col-md-6">
                <Select
                  label="Contact type"
                  placeholder="Select…"
                  multiple
                  searchable
                  clearable
                  options={contactTypes.map((type) => ({ value: type.id, label: type.name }))}
                  value={addContactTypeIds}
                  onChange={(value) => setAddContactTypeIds(value || [])}
                />
              </div>
            </div>
          </div>
        )}

        {contactModalTab === 'existing' && (
          <div style={{ marginTop: 16 }}>
            {attachError && (
              <div style={{ marginBottom: 12 }}>
                <Alert color="error" description={attachError} />
              </div>
            )}

            <p className="text-muted small">Search for an existing contact to add to this deal.</p>

            <div className="d-flex" style={{ gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <TextInput
                  placeholder="Search contacts by name"
                  value={attachSearch}
                  onChange={(e) => setAttachSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAttachSearch(); } }}
                />
              </div>
              <Button variant="secondary" onClick={handleAttachSearch} disabled={attachLoading}>
                {attachLoading ? 'Searching…' : 'Search'}
              </Button>
            </div>

            <div style={{ maxHeight: 260, overflowY: 'auto' }}>
              <Table
                columns={[
                  {
                    key: 'select',
                    header: '',
                    width: '40px',
                    render: (result) => (
                      <input
                        type="radio"
                        className="team-checkbox"
                        checked={attachSelectedId === result.id}
                        onChange={() => setAttachSelectedId(result.id)}
                        aria-label={`Select ${result.name}`}
                      />
                    ),
                  },
                  { key: 'name', header: 'Name', render: (result) => result.name },
                  { key: 'details', header: 'Details', render: (result) => result.title_email_preview || '—' },
                ]}
                data={attachResults}
                getRowKey={(result) => result.id}
                empty={(
                  <EmptyState
                    size="sm"
                    icon={User01}
                    title={attachSearch.trim() ? 'No matching contacts found.' : 'Search to find contacts.'}
                  />
                )}
              />
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={showNoteModal}
        onClose={() => { setShowNoteModal(false); setEditingNoteId(null); }}
        icon={false}
        width={560}
        title={editingNoteId ? 'Edit note' : 'Add note'}
        footer={(
          <>
            <Button
              variant="secondary"
              onClick={() => { setShowNoteModal(false); setEditingNoteId(null); }}
              disabled={noteSubmitting}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveNote} disabled={!canSubmitNoteForm || noteSubmitting}>
              {noteSubmitting ? 'Saving…' : (editingNoteId ? 'Update note' : 'Save note')}
            </Button>
          </>
        )}
      >
        {noteErrors.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Alert color="error" description={noteErrors.join(', ')} />
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <TextInput
            label="Title (optional)"
            value={noteTitle}
            onChange={(e) => setNoteTitle(e.target.value)}
            placeholder="Enter note title"
          />
        </div>

        <TextInput
          label="Note"
          required
          multiline
          rows={4}
          value={noteBody}
          onChange={(e) => setNoteBody(e.target.value)}
          placeholder="Enter note content"
        />
      </Modal>
    </CrmPageLayout>
  );
}

export default DealsDetailPage;
